/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { AccountIdTopicsBody } from '../models';
import { InlineResponse2007 } from '../models';
import { InlineResponse20110 } from '../models';
import { InlineResponse2016 } from '../models';
import { InlineResponse2017 } from '../models';
import { InlineResponse2018 } from '../models';
import { InlineResponse2019 } from '../models';
import { SuggestionURL } from '../models';
import { Topic } from '../models';
import { TopicIdAccountBody } from '../models';
import { TopicIdNameBody } from '../models';
import { TopicIdOwnerBody } from '../models';
import { TopicIdParentBody } from '../models';
import { TopicIdQueriesBody } from '../models';
import { TopicURL } from '../models';
import { TopicsBody } from '../models';
import { UserURL } from '../models';
/**
 * TopicsApi - axios parameter creator
 * @export
 */
export const TopicsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of active topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active topics from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdActiveTopicsGet: async (account_id: string, folder?: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdActiveTopicsGet.');
            }
            const localVarPath = `/accounts/{account_id}/active-topics`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            if (folder !== undefined) {
                localVarQueryParameter['folder'] = folder;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get topics from
         * @param {boolean} [deactivated] If true, get only deactivated topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdTopicsGet: async (account_id: string, folder?: string, deactivated?: boolean, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdTopicsGet.');
            }
            const localVarPath = `/accounts/{account_id}/topics`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            if (folder !== undefined) {
                localVarQueryParameter['folder'] = folder;
            }

            if (deactivated !== undefined) {
                localVarQueryParameter['deactivated'] = deactivated;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create a topic in this account
         * @param {AccountIdTopicsBody} body Data for creating a topic in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdTopicsPost: async (body: AccountIdTopicsBody, account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling accountsAccountIdTopicsPost.');
            }
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdTopicsPost.');
            }
            const localVarPath = `/accounts/{account_id}/topics`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deactivate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activeTopicsTopicIdDelete: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling activeTopicsTopicIdDelete.');
            }
            const localVarPath = `/active-topics/{topic_id}`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Activate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activeTopicsTopicIdPut: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling activeTopicsTopicIdPut.');
            }
            const localVarPath = `/active-topics/{topic_id}`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the starter topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        starterTopicsGet: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/starter-topics`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of the current user's topics.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsGet: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/topics`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create a topic
         * @param {TopicsBody} body Data for creating a topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsPost: async (body: TopicsBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsPost.');
            }
            const localVarPath = `/topics`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Transfer the topic to a different account
         * @param {TopicIdAccountBody} body Data for transferring topic to a different account
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdAccountPut: async (body: TopicIdAccountBody, topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsTopicIdAccountPut.');
            }
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdAccountPut.');
            }
            const localVarPath = `/topics/{topic_id}/account`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdDelete: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdDelete.');
            }
            const localVarPath = `/topics/{topic_id}`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of favorited suggestions in this topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdFavoriteSuggestionsGet: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdFavoriteSuggestionsGet.');
            }
            const localVarPath = `/topics/{topic_id}/favorite-suggestions`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdGet: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdGet.');
            }
            const localVarPath = `/topics/{topic_id}`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the topic name
         * @param {TopicIdNameBody} body Data for updating the topic name
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdNamePut: async (body: TopicIdNameBody, topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsTopicIdNamePut.');
            }
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdNamePut.');
            }
            const localVarPath = `/topics/{topic_id}/name`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the topic owner
         * @param {TopicIdOwnerBody} body Data for updating the topic owner
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdOwnerPut: async (body: TopicIdOwnerBody, topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsTopicIdOwnerPut.');
            }
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdOwnerPut.');
            }
            const localVarPath = `/topics/{topic_id}/owner`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Move the topic to a new folder
         * @param {TopicIdParentBody} body Data for updating the topic&#x27;s parent folder
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdParentPut: async (body: TopicIdParentBody, topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsTopicIdParentPut.');
            }
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdParentPut.');
            }
            const localVarPath = `/topics/{topic_id}/parent`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the topic queries
         * @param {TopicIdQueriesBody} body Data for updating the set of topic queries
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdQueriesPut: async (body: TopicIdQueriesBody, topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling topicsTopicIdQueriesPut.');
            }
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdQueriesPut.');
            }
            const localVarPath = `/topics/{topic_id}/queries`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get this topic's suggestions
         * @param {string} topic_id UUID for the topic
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {boolean} [undated] Include undated suggestions
         * @param {Array<string>} [sources] Only include suggestions from these sources
         * @param {Array<string>} [excludes] Filter out suggestions from these sources
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdSuggestionsGet: async (topic_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, undated?: boolean, sources?: Array<string>, excludes?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdSuggestionsGet.');
            }
            const localVarPath = `/topics/{topic_id}/suggestions`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (type !== undefined) {
                localVarQueryParameter['type'] = type;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (age !== undefined) {
                localVarQueryParameter['age'] = age;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            if (undated !== undefined) {
                localVarQueryParameter['undated'] = undated;
            }

            if (sources) {
                localVarQueryParameter['sources'] = sources.join(COLLECTION_FORMATS.csv);
            }

            if (excludes) {
                localVarQueryParameter['excludes'] = excludes.join(COLLECTION_FORMATS.csv);
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of users which can access the topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        topicsTopicIdUsersGet: async (topic_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'topic_id' is not null or undefined
            if (topic_id === null || topic_id === undefined) {
                throw new RequiredError('topic_id','Required parameter topic_id was null or undefined when calling topicsTopicIdUsersGet.');
            }
            const localVarPath = `/topics/{topic_id}/users`
                .replace(`{${"topic_id"}}`, encodeURIComponent(String(topic_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TopicsApi - functional programming interface
 * @export
 */
export const TopicsApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of active topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active topics from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdActiveTopicsGet(account_id: string, folder?: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<TopicURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).accountsAccountIdActiveTopicsGet(account_id, folder, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get topics from
         * @param {boolean} [deactivated] If true, get only deactivated topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdTopicsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<TopicURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).accountsAccountIdTopicsGet(account_id, folder, deactivated, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create a topic in this account
         * @param {AccountIdTopicsBody} body Data for creating a topic in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdTopicsPost(body: AccountIdTopicsBody, account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Topic>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).accountsAccountIdTopicsPost(body, account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Deactivate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeTopicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).activeTopicsTopicIdDelete(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Activate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeTopicsTopicIdPut(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).activeTopicsTopicIdPut(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the starter topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async starterTopicsGet(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<InlineResponse2007>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).starterTopicsGet(options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of the current user's topics.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsGet(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<TopicURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsGet(options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create a topic
         * @param {TopicsBody} body Data for creating a topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsPost(body: TopicsBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Topic>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsPost(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Transfer the topic to a different account
         * @param {TopicIdAccountBody} body Data for transferring topic to a different account
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdAccountPut(body: TopicIdAccountBody, topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2016>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdAccountPut(body, topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Delete a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdDelete(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of favorited suggestions in this topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdFavoriteSuggestionsGet(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<SuggestionURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdFavoriteSuggestionsGet(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdGet(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Topic>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdGet(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the topic name
         * @param {TopicIdNameBody} body Data for updating the topic name
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdNamePut(body: TopicIdNameBody, topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2018>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdNamePut(body, topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the topic owner
         * @param {TopicIdOwnerBody} body Data for updating the topic owner
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdOwnerPut(body: TopicIdOwnerBody, topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2017>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdOwnerPut(body, topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Move the topic to a new folder
         * @param {TopicIdParentBody} body Data for updating the topic&#x27;s parent folder
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdParentPut(body: TopicIdParentBody, topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20110>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdParentPut(body, topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the topic queries
         * @param {TopicIdQueriesBody} body Data for updating the set of topic queries
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdQueriesPut(body: TopicIdQueriesBody, topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2019>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdQueriesPut(body, topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get this topic's suggestions
         * @param {string} topic_id UUID for the topic
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {boolean} [undated] Include undated suggestions
         * @param {Array<string>} [sources] Only include suggestions from these sources
         * @param {Array<string>} [excludes] Filter out suggestions from these sources
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdSuggestionsGet(topic_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, undated?: boolean, sources?: Array<string>, excludes?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<SuggestionURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdSuggestionsGet(topic_id, type, sort, age, limit, offset, undated, sources, excludes, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of users which can access the topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdUsersGet(topic_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<UserURL>>>> {
            const localVarAxiosArgs = await TopicsApiAxiosParamCreator(configuration).topicsTopicIdUsersGet(topic_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * TopicsApi - factory interface
 * @export
 */
export const TopicsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Get the list of active topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get active topics from
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdActiveTopicsGet(account_id: string, folder?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<TopicURL>>> {
            return TopicsApiFp(configuration).accountsAccountIdActiveTopicsGet(account_id, folder, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of topics in this account
         * @param {string} account_id UUID for the account
         * @param {string} [folder] UUID of the folder to get topics from
         * @param {boolean} [deactivated] If true, get only deactivated topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdTopicsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<TopicURL>>> {
            return TopicsApiFp(configuration).accountsAccountIdTopicsGet(account_id, folder, deactivated, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create a topic in this account
         * @param {AccountIdTopicsBody} body Data for creating a topic in this account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdTopicsPost(body: AccountIdTopicsBody, account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Topic>> {
            return TopicsApiFp(configuration).accountsAccountIdTopicsPost(body, account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deactivate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeTopicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return TopicsApiFp(configuration).activeTopicsTopicIdDelete(topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Activate a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activeTopicsTopicIdPut(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return TopicsApiFp(configuration).activeTopicsTopicIdPut(topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the starter topics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async starterTopicsGet(options?: AxiosRequestConfig): Promise<AxiosResponse<Array<InlineResponse2007>>> {
            return TopicsApiFp(configuration).starterTopicsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of the current user's topics.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsGet(options?: AxiosRequestConfig): Promise<AxiosResponse<Array<TopicURL>>> {
            return TopicsApiFp(configuration).topicsGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create a topic
         * @param {TopicsBody} body Data for creating a topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsPost(body: TopicsBody, options?: AxiosRequestConfig): Promise<AxiosResponse<Topic>> {
            return TopicsApiFp(configuration).topicsPost(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Transfer the topic to a different account
         * @param {TopicIdAccountBody} body Data for transferring topic to a different account
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdAccountPut(body: TopicIdAccountBody, topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2016>> {
            return TopicsApiFp(configuration).topicsTopicIdAccountPut(body, topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return TopicsApiFp(configuration).topicsTopicIdDelete(topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of favorited suggestions in this topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdFavoriteSuggestionsGet(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<SuggestionURL>>> {
            return TopicsApiFp(configuration).topicsTopicIdFavoriteSuggestionsGet(topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdGet(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Topic>> {
            return TopicsApiFp(configuration).topicsTopicIdGet(topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the topic name
         * @param {TopicIdNameBody} body Data for updating the topic name
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdNamePut(body: TopicIdNameBody, topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2018>> {
            return TopicsApiFp(configuration).topicsTopicIdNamePut(body, topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the topic owner
         * @param {TopicIdOwnerBody} body Data for updating the topic owner
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdOwnerPut(body: TopicIdOwnerBody, topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2017>> {
            return TopicsApiFp(configuration).topicsTopicIdOwnerPut(body, topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Move the topic to a new folder
         * @param {TopicIdParentBody} body Data for updating the topic&#x27;s parent folder
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdParentPut(body: TopicIdParentBody, topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20110>> {
            return TopicsApiFp(configuration).topicsTopicIdParentPut(body, topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the topic queries
         * @param {TopicIdQueriesBody} body Data for updating the set of topic queries
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdQueriesPut(body: TopicIdQueriesBody, topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2019>> {
            return TopicsApiFp(configuration).topicsTopicIdQueriesPut(body, topic_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get this topic's suggestions
         * @param {string} topic_id UUID for the topic
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {boolean} [undated] Include undated suggestions
         * @param {Array<string>} [sources] Only include suggestions from these sources
         * @param {Array<string>} [excludes] Filter out suggestions from these sources
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdSuggestionsGet(topic_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, undated?: boolean, sources?: Array<string>, excludes?: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<SuggestionURL>>> {
            return TopicsApiFp(configuration).topicsTopicIdSuggestionsGet(topic_id, type, sort, age, limit, offset, undated, sources, excludes, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of users which can access the topic
         * @param {string} topic_id UUID for the topic
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async topicsTopicIdUsersGet(topic_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<UserURL>>> {
            return TopicsApiFp(configuration).topicsTopicIdUsersGet(topic_id, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * TopicsApi - object-oriented interface
 * @export
 * @class TopicsApi
 * @extends {BaseAPI}
 */
export class TopicsApi extends BaseAPI {
    /**
     * 
     * @summary Get the list of active topics in this account
     * @param {string} account_id UUID for the account
     * @param {string} [folder] UUID of the folder to get active topics from
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async accountsAccountIdActiveTopicsGet(account_id: string, folder?: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<TopicURL>>> {
        return TopicsApiFp(this.configuration).accountsAccountIdActiveTopicsGet(account_id, folder, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of topics in this account
     * @param {string} account_id UUID for the account
     * @param {string} [folder] UUID of the folder to get topics from
     * @param {boolean} [deactivated] If true, get only deactivated topics
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async accountsAccountIdTopicsGet(account_id: string, folder?: string, deactivated?: boolean, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<TopicURL>>> {
        return TopicsApiFp(this.configuration).accountsAccountIdTopicsGet(account_id, folder, deactivated, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create a topic in this account
     * @param {AccountIdTopicsBody} body Data for creating a topic in this account
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async accountsAccountIdTopicsPost(body: AccountIdTopicsBody, account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Topic>> {
        return TopicsApiFp(this.configuration).accountsAccountIdTopicsPost(body, account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Deactivate a topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async activeTopicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return TopicsApiFp(this.configuration).activeTopicsTopicIdDelete(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Activate a topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async activeTopicsTopicIdPut(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return TopicsApiFp(this.configuration).activeTopicsTopicIdPut(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the starter topics
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async starterTopicsGet(options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<InlineResponse2007>>> {
        return TopicsApiFp(this.configuration).starterTopicsGet(options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of the current user's topics.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsGet(options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<TopicURL>>> {
        return TopicsApiFp(this.configuration).topicsGet(options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create a topic
     * @param {TopicsBody} body Data for creating a topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsPost(body: TopicsBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<Topic>> {
        return TopicsApiFp(this.configuration).topicsPost(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Transfer the topic to a different account
     * @param {TopicIdAccountBody} body Data for transferring topic to a different account
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdAccountPut(body: TopicIdAccountBody, topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2016>> {
        return TopicsApiFp(this.configuration).topicsTopicIdAccountPut(body, topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Delete a topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdDelete(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return TopicsApiFp(this.configuration).topicsTopicIdDelete(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of favorited suggestions in this topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdFavoriteSuggestionsGet(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<SuggestionURL>>> {
        return TopicsApiFp(this.configuration).topicsTopicIdFavoriteSuggestionsGet(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdGet(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Topic>> {
        return TopicsApiFp(this.configuration).topicsTopicIdGet(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the topic name
     * @param {TopicIdNameBody} body Data for updating the topic name
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdNamePut(body: TopicIdNameBody, topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2018>> {
        return TopicsApiFp(this.configuration).topicsTopicIdNamePut(body, topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the topic owner
     * @param {TopicIdOwnerBody} body Data for updating the topic owner
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdOwnerPut(body: TopicIdOwnerBody, topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2017>> {
        return TopicsApiFp(this.configuration).topicsTopicIdOwnerPut(body, topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Move the topic to a new folder
     * @param {TopicIdParentBody} body Data for updating the topic&#x27;s parent folder
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdParentPut(body: TopicIdParentBody, topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20110>> {
        return TopicsApiFp(this.configuration).topicsTopicIdParentPut(body, topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the topic queries
     * @param {TopicIdQueriesBody} body Data for updating the set of topic queries
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdQueriesPut(body: TopicIdQueriesBody, topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2019>> {
        return TopicsApiFp(this.configuration).topicsTopicIdQueriesPut(body, topic_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get this topic's suggestions
     * @param {string} topic_id UUID for the topic
     * @param {string} [type] Suggestion type to return
     * @param {string} [sort] Sorting method to use
     * @param {number} [age] Maximum age (in days) of suggestions to return
     * @param {number} [limit] Maximum number of suggestions to return
     * @param {number} [offset] Starting position offset of the list of suggestions to return
     * @param {boolean} [undated] Include undated suggestions
     * @param {Array<string>} [sources] Only include suggestions from these sources
     * @param {Array<string>} [excludes] Filter out suggestions from these sources
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdSuggestionsGet(topic_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, undated?: boolean, sources?: Array<string>, excludes?: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<SuggestionURL>>> {
        return TopicsApiFp(this.configuration).topicsTopicIdSuggestionsGet(topic_id, type, sort, age, limit, offset, undated, sources, excludes, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of users which can access the topic
     * @param {string} topic_id UUID for the topic
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof TopicsApi
     */
    public async topicsTopicIdUsersGet(topic_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<UserURL>>> {
        return TopicsApiFp(this.configuration).topicsTopicIdUsersGet(topic_id, options).then((request) => request(this.axios, this.basePath));
    }
}
